import { useState } from 'react';
import {
  AlarmIcon,
  ArrowsClockwiseIcon,
  CaretDownIcon,
  UserSwitchIcon,
  XIcon,
} from '@phosphor-icons/react';
import { T, useGT, Var } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { useCan } from 'src/hooks/useCan';
import { TaskQueueReassignDialog } from './TaskQueueReassignDialog';
import { TaskQueueReturnToAgentDialog } from './TaskQueueReturnToAgentDialog';
import type { TaskQueueBulkActions } from './useTaskQueueBulkActions';

/**
 * Selection bar shown above the tasks-queue table while rows are selected.
 * "Reassign" moves the open tasks to one person or agent. "Return to Agent"
 * acts only on failed / needs_input tasks an agent previously worked; the
 * remainder is reported as skipped so a broad select-all is safe. Its caret
 * menu offers "Return to a different agent", which re-queues the failed /
 * needs_input selection with an agent the user picks.
 */
export function TaskQueueBulkActionsBar({
  bulk,
  onSnooze,
}: {
  bulk: TaskQueueBulkActions;
  onSnooze: (taskIds: string[]) => void;
}) {
  const gt = useGT();
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [otherAgentOpen, setOtherAgentOpen] = useState(false);
  // Selection dropped (cleared, or the page/filter changed): close the dialogs
  // so the next selection does not reopen them.
  if (bulk.selectedCount === 0 && (reassignOpen || otherAgentOpen)) {
    setReassignOpen(false);
    setOtherAgentOpen(false);
  }
  if (bulk.selectedCount === 0) return null;
  const skippedCount = bulk.selectedCount - bulk.returnableCount;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-accent px-3 py-1.5 text-xs">
        <span className="font-medium">
          <T context="selected-row count on the tasks queue bulk action bar">
            <Var>{bulk.selectedCount}</Var> selected
          </T>
        </span>
        {skippedCount > 0 && (
          <span className="text-muted-foreground">
            <T context="hint on the tasks queue bulk bar: how many selected rows the return-to-agent action will skip and why">
              <Var>{skippedCount}</Var> skipped — only failed or needs-input
              tasks that an agent worked can be returned
            </T>
          </span>
        )}
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          disabled={bulk.isReturning}
          onClick={() => onSnooze(bulk.selectedIds)}
        >
          <AlarmIcon className="h-4 w-4" />
          <T context="bulk action button: snooze every selected task">Snooze</T>
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={bulk.reassignableCount === 0 || bulk.isReturning}
          onClick={() => setReassignOpen(true)}
        >
          <UserSwitchIcon className="h-4 w-4" />
          <T context="bulk action button: reassign the selected open tasks to one person or agent">
            Reassign (<Var>{bulk.reassignableCount}</Var>)
          </T>
        </Button>
        <div className="flex">
          <Button
            variant="outline"
            size="sm"
            className={canReadAgents ? 'rounded-r-none' : undefined}
            disabled={bulk.returnableCount === 0 || bulk.isReturning}
            onClick={bulk.returnSelectedToAgent}
          >
            <ArrowsClockwiseIcon className="h-4 w-4" />
            <T context="bulk action button: re-queue the selected failed / needs-input tasks with the agent that last worked them">
              Return to Agent (<Var>{bulk.returnableCount}</Var>)
            </T>
          </Button>
          {canReadAgents && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-l-none border-l-0 px-1.5"
                  disabled={bulk.isReturning}
                  aria-label={gt('More return-to-agent options', {
                    context:
                      'caret button next to Return to Agent on the tasks queue bulk bar',
                  })}
                >
                  <CaretDownIcon className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={bulk.otherAgentReturnableCount === 0}
                  onClick={() => setOtherAgentOpen(true)}
                >
                  <ArrowsClockwiseIcon className="h-4 w-4" />
                  <T context="bulk menu item: send the selected failed / needs-input tasks to an agent the user picks">
                    Return to a different agent (
                    <Var>{bulk.otherAgentReturnableCount}</Var>)
                  </T>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={bulk.isReturning}
          onClick={bulk.clearSelection}
          aria-label={gt('Clear selection', {
            context: 'button, clear the selected rows on the tasks queue',
          })}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </div>
      <TaskQueueReassignDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        taskIds={bulk.reassignableIds}
        onAssigned={bulk.clearSelection}
      />
      <TaskQueueReturnToAgentDialog
        open={otherAgentOpen}
        onOpenChange={setOtherAgentOpen}
        taskCount={bulk.otherAgentReturnableCount}
        isBusy={bulk.isReturning}
        onConfirm={bulk.returnSelectedToOtherAgent}
      />
    </>
  );
}
